// Datadog browser logger wrapper providing a server-safe no-op fallback.
// The '@datadog/browser-logs' package accesses window/document; importing or
// initializing it during an SSR/Node execution path (API routes, getServerSideProps)
// causes ReferenceError: document is not defined. We therefore lazily access it
// only when running in a browser and expose a minimal logger interface otherwise.

import { DATADOG_API_TOKEN, DATADOG_ENVIRONMENT } from "lib/constants";

type LoggerLike = {
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  log: (...args: any[]) => void;
};

const noop: LoggerLike = {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
  log: () => {},
};

// Use 'any' for the underlying Datadog logs API to avoid brittle type import mismatches.
// We only access a small stable surface (init, logger).
type DatadogLogsAny = any;
let datadogLogsSingleton: DatadogLogsAny | null = null;

function loadDatadog(): DatadogLogsAny | null {
  if (datadogLogsSingleton) return datadogLogsSingleton;
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  // Dynamically require to avoid evaluating module in Node.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { datadogLogs } = require("@datadog/browser-logs");
  datadogLogsSingleton = datadogLogs as DatadogLogsAny;
  return datadogLogsSingleton;
}

export const initDatadogLogger = () => {
  const dd = loadDatadog();
  if (!dd) return; // server: skip
  if ((dd as any)._siftedInitialized) return; // idempotent
  dd.init({
    clientToken: DATADOG_API_TOKEN,
    site: "datadoghq.eu",
    service: "content-ui",
    env: DATADOG_ENVIRONMENT || process.env.NODE_ENV,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: () => {
      if (
        typeof window?.Cookiebot?.consent?.statistics === "boolean" &&
        !window.Cookiebot.consent.statistics
      ) {
        return false;
      }
    },
  });
  (dd as any)._siftedInitialized = true;
};

export const datadogLogger: LoggerLike = (() => {
  const dd = loadDatadog();
  return dd ? (dd.logger as LoggerLike) : noop;
})();
